import { Flex, Text } from "@/atoms"
import { ToolOutlined } from "@/icons"
import { StatusTag } from "@/molecules/StatusTag"
import { WorkloadDecisionCell } from "@/pages/GovernancePage/components/molecules/AiPolicyDecision"
import { AgentCell } from "@/pages/GovernancePage/components/pages/AiPoliciesPage/components/AgentCell"

import type { RowClick } from "@/hooks/useLinkDrawerTooltip"
import type { TFunction } from "@/hooks/useTranslation"
import type { AiPolicyDecisionRow } from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies.types"

// onRowClick opens the agent drawer of a workload's agent
export const getColumnsTable = (t: TFunction, onRowClick: RowClick) => {
  const tKey = "governance.ai.live.violationLog"

  return [
    {
      title: t(`${tKey}.columns.detected`),
      key: "detected",
      width: 190,
      render: (_: unknown, { decided }: AiPolicyDecisionRow) =>
        decided.tooltip ?? decided.text
    },
    {
      title: t(`${tKey}.columns.workload`),
      key: "workload",
      width: 200,
      // Where the call was caught: the Kubernetes workload the sensor reported
      render: (_: unknown, { workloadLabel }: AiPolicyDecisionRow) =>
        workloadLabel ?? (
          <Text type="secondary">{t(`${tKey}.notAvailable`)}</Text>
        )
    },
    {
      // What the violation is on: an agent; an MCP server policy's MCP server,
      // named only; or a workload, with its agents
      title: t(`${tKey}.columns.resource`),
      key: "agent",
      width: 240,
      // A decision whose agent the platform did not report says so
      render: (_: unknown, row: AiPolicyDecisionRow) => {
        const { agent, mcpServerName } = row

        // The popup keeps its clicks: an agent opens, not the row's details
        if (row.subject === "workload")
          return WorkloadDecisionCell(t, onRowClick, row)

        return mcpServerName ? (
          mcpServerName
        ) : agent ? (
          AgentCell(agent)
        ) : (
          <Text type="secondary">
            {t("governance.ai.live.decisions.notReported")}
          </Text>
        )
      }
    },
    {
      title: t(`${tKey}.columns.violation`),
      key: "violation",
      // What broke the rule: the tool called, the one value of the call the
      // decisions record
      render: (_: unknown, { tool }: AiPolicyDecisionRow) =>
        tool ? (
          <Flex align="center" gap={6}>
            <ToolOutlined />
            {tool}
          </Flex>
        ) : (
          <Text type="secondary">{t(`${tKey}.notAvailable`)}</Text>
        )
    },
    {
      title: t(`${tKey}.columns.actionTaken`),
      key: "actionTaken",
      width: 150,
      render: (_: unknown, { blocked }: AiPolicyDecisionRow) =>
        blocked ? (
          <StatusTag status="failed" text={t(`${tKey}.actions.blocked`)} />
        ) : (
          <StatusTag status="neutral" text={t(`${tKey}.actions.monitored`)} />
        )
    }
  ]
}
